import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, RefreshCw, Undo2 } from "lucide-react";
import { ASSETS } from "@/data/site";
import { DECK } from "@/data/tarotDeck";

const GuidanceCard = () => {
  const [card, setCard] = useState(null);
  const [seen, setSeen] = useState([]);
  const [imgError, setImgError] = useState(false);

  const drawNext = (prevSeen) => {
    // Pool = cards not yet seen. If exhausted, reset the pool.
    let pool = DECK.filter((c) => !prevSeen.includes(c.id));
    let nextSeen = prevSeen;
    if (pool.length === 0) {
      pool = DECK;
      nextSeen = [];
    }
    const next = pool[Math.floor(Math.random() * pool.length)];
    return { next, nextSeen: [...nextSeen, next.id] };
  };

  const handleDraw = () => {
    setImgError(false);
    const { next, nextSeen } = drawNext(seen);
    setCard(next);
    setSeen(nextSeen);
  };

  const handleAnother = () => {
    setImgError(false);
    const { next, nextSeen } = drawNext(seen);
    setCard(next);
    setSeen(nextSeen);
  };

  const handleReturn = () => {
    setImgError(false);
    setCard(null);
  };

  return (
    <div className="relative max-w-3xl mx-auto" data-testid="guidance-card-section">
      <div className="text-center mb-8">
        <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: "var(--cs-purple)" }}>
          A moment with the cards
        </p>
        <h3 className="font-serif-display text-3xl md:text-4xl leading-tight" style={{ color: "var(--cs-blue-dk)" }}>
          What is your <span className="italic" style={{ color: "var(--cs-purple)" }}>guidance for today?</span>
        </h3>
        <p className="mt-3 text-base" style={{ color: "#475569" }}>
          Take a breath, hold a question gently in mind, and click the card.
        </p>
      </div>

      <div className="flex flex-col items-center">
        <AnimatePresence mode="wait">
          {!card ? (
            <motion.button
              key="back"
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              transition={{ duration: 0.5 }}
              whileHover={{ y: -6, scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleDraw}
              className="block focus:outline-none rounded-2xl"
              data-testid="guidance-draw-btn"
              aria-label="Draw a guidance card"
            >
              <img
                src={ASSETS.tarotBack}
                alt="Tarot card back — click to reveal your card"
                className="w-52 md:w-64 rounded-2xl"
                style={{ boxShadow: "0 20px 60px rgba(153, 76, 153, 0.35), 0 0 0 1px rgba(19,26,72,0.10)" }}
              />
              <span className="mt-5 inline-flex items-center gap-2 btn-pill-primary text-base">
                <Sparkles className="w-4 h-4" /> Reveal my card
              </span>
            </motion.button>
          ) : (
            <motion.div
              key={card.id}
              initial={{ rotateY: 180, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.7 }}
              className="w-full"
              data-testid="guidance-card-result"
            >
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="flex justify-center md:justify-end">
                  {!imgError ? (
                    <img
                      src={card.imageUrl}
                      alt={card.name}
                      onError={() => setImgError(true)}
                      className="w-52 md:w-60 rounded-2xl"
                      style={{ boxShadow: "0 20px 60px rgba(153, 76, 153, 0.35), 0 0 0 1px rgba(19,26,72,0.10)" }}
                    />
                  ) : (
                    <div className="w-52 md:w-60 aspect-[5/8] rounded-2xl flex items-center justify-center text-center p-5"
                         style={{ background: "linear-gradient(135deg,#25478e,#131a48)", color: "#fff", fontFamily: "'Cormorant Garamond', serif" }}>
                      <div>
                        <div className="text-xs uppercase tracking-widest opacity-70 mb-1">
                          {card.arcana === "major" ? "Major" : card.suit}
                        </div>
                        <div className="text-2xl italic">{card.name}</div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-center md:text-left">
                  <p className="text-xs uppercase tracking-[0.3em] mb-2" style={{ color: "var(--cs-purple)" }}>
                    {card.arcana === "major" ? `MAJOR ARCANA · ${card.n}` : `${card.suit.toUpperCase()} · ${card.label}`}
                  </p>
                  <h4 className="font-serif-display text-3xl md:text-4xl mb-3" style={{ color: "var(--cs-navy)" }} data-testid="guidance-card-name">
                    {card.name}
                  </h4>
                  <div className="flex flex-wrap gap-2 mb-4 justify-center md:justify-start">
                    {card.keywords.slice(0, 4).map((k) => (
                      <span key={k} className="text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full"
                            style={{ background: "rgba(153,76,153,0.08)", border: "1px solid rgba(153,76,153,0.20)", color: "var(--cs-purple)" }}>
                        {k}
                      </span>
                    ))}
                  </div>
                  <p className="leading-relaxed text-base" style={{ color: "#334155" }}>
                    <span className="font-medium" style={{ color: "var(--cs-blue-dk)" }}>Today's guidance: </span>
                    {card.upright}
                  </p>

                  <div className="flex flex-wrap gap-3 mt-6 justify-center md:justify-start">
                    <button onClick={handleAnother} className="btn-pill-ghost-dark text-sm" data-testid="guidance-redraw-btn">
                      <RefreshCw className="w-4 h-4" /> Draw another card
                    </button>
                    <button onClick={handleReturn} className="btn-pill-ghost-dark text-sm" data-testid="guidance-return-btn">
                      <Undo2 className="w-4 h-4" /> Return to deck
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default GuidanceCard;
